export const SORT_OPTIONS = {
  newest: 'newest',
  oldest: 'oldest',
  nameAsc: 'name-asc',
  nameDesc: 'name-desc'
};

export const getCourseDateValue = (course, field) => {
  const dateValue = course[field] || course[field.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)];
  if (!dateValue) return 0;

  const timestamp = new Date(dateValue).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

export const sortCourses = (courses, sortBy) => {
  return [...courses].sort((a, b) => {
    if (sortBy === SORT_OPTIONS.nameAsc) {
      return a.name.localeCompare(b.name);
    }

    if (sortBy === SORT_OPTIONS.nameDesc) {
      return b.name.localeCompare(a.name);
    }

    const aStartDate = getCourseDateValue(a, 'startDate');
    const bStartDate = getCourseDateValue(b, 'startDate');
    const aEndDate = getCourseDateValue(a, 'endDate');
    const bEndDate = getCourseDateValue(b, 'endDate');

    if (sortBy === SORT_OPTIONS.oldest) {
      return aStartDate - bStartDate || aEndDate - bEndDate || a.name.localeCompare(b.name);
    }

    return bStartDate - aStartDate || bEndDate - aEndDate || a.name.localeCompare(b.name);
  });
};

export const formatDate = (dateString) => {
  if (!dateString) return 'TBD';
  // Parse date-only strings without timezone conversion.
  const [year, month, day] = dateString.split('T')[0].split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
};

// "SHM101 - 10/21/2026": the course code and when it starts (no start date: just the code).
export const courseHistoryDescription = (courseCode, startDate) =>
  startDate ? `${courseCode} - ${formatDate(startDate)}` : courseCode;

export const parseCourseDate = (dateString) => {
  if (!dateString) return null;

  const [year, month, day] = String(dateString).split('T')[0].split('-');
  if (!year || !month || !day) return null;

  const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
  return Number.isNaN(date.getTime()) ? null : date;
};

export const isPreviousCourse = (course) => {
  const endDate = parseCourseDate(course.endDate || course.end_date);
  if (!endDate) return false;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return endDate < today;
};

// The catalog cart (selected course codes / product ids) is kept in the browser so a
// refresh, or the sign-in round trip, doesn't empty it. Storage can be blocked or
// full, so every access is guarded and the cart simply isn't saved in that case.
export const CART_STORAGE_KEY = 'faithinsight_catalog_cart';

export const readSavedCart = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(CART_STORAGE_KEY) || 'null');
    const list = (value) => (Array.isArray(value) ? value.map(String) : []);
    return { courses: list(parsed?.courses), products: list(parsed?.products) };
  } catch (error) {
    return { courses: [], products: [] };
  }
};

export const saveCart = (courses, products) => {
  try {
    if (courses.length === 0 && products.length === 0) {
      localStorage.removeItem(CART_STORAGE_KEY);
    } else {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify({
        courses: courses.map((course) => course.code),
        products: products.map((product) => product.id)
      }));
    }
  } catch (error) {
    // Cart just isn't persisted.
  }
};

export const clearSavedCart = () => {
  try {
    localStorage.removeItem(CART_STORAGE_KEY);
  } catch (error) {
    // Nothing to clear.
  }
};

// ---- Catalog presentation helpers ----

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const startOf = (course) => parseCourseDate(course.startDate || course.start_date);
const endOf = (course) => parseCourseDate(course.endDate || course.end_date);

// "Oct 22"
export const formatShortDate = (date) => (date ? `${MONTHS[date.getMonth()]} ${date.getDate()}` : '');

// "Oct 22 – Dec 10, 2026" (both years when the course crosses a year boundary).
export const formatCourseRange = (course) => {
  const start = startOf(course);
  const end = endOf(course);
  if (!start) return '';
  if (!end) return `${formatShortDate(start)}, ${start.getFullYear()}`;
  if (start.getFullYear() === end.getFullYear()) {
    return `${formatShortDate(start)} – ${formatShortDate(end)}, ${end.getFullYear()}`;
  }
  return `${formatShortDate(start)}, ${start.getFullYear()} – ${formatShortDate(end)}, ${end.getFullYear()}`;
};

// "8 weeks" (null when either date is missing). Both the first and last day count, and a part week
// counts as a week: Oct 22 - Dec 10 is 50 days, so 8 weeks.
export const getCourseWeeks = (course) => {
  const start = startOf(course);
  const end = endOf(course);
  if (!start || !end || end < start) return null;
  const days = Math.round((end - start) / (24 * 60 * 60 * 1000)) + 1;
  const weeks = Math.ceil(days / 7);
  return `${weeks} ${weeks === 1 ? 'week' : 'weeks'}`;
};

// "Summer 2026". Labelled by the middle of the course's dates, not its start month: a course that runs
// Jan 8 - Jun 5 is the spring term even though it starts in January. With no end date, the start is used.
// (Dec-Feb winter, Mar-May spring, Jun-Aug summer, Sep-Nov fall.)
export const getSeasonLabel = (course) => {
  const start = startOf(course);
  if (!start) return '';
  const end = endOf(course);
  const middle = end && end >= start ? new Date((start.getTime() + end.getTime()) / 2) : start;
  const month = middle.getMonth();
  let season = 'Fall';
  if (month === 11 || month <= 1) season = 'Winter';
  else if (month <= 4) season = 'Spring';
  else if (month <= 7) season = 'Summer';
  return `${season} ${middle.getFullYear()}`;
};

// True once the course has started (an active course that is already running).
export const hasCourseStarted = (course) => {
  const start = startOf(course);
  if (!start) return false;
  const now = new Date();
  return start <= new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

export const getInitials = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
};

// Whole dollars without cents, otherwise two decimals: 100 -> "$100", 92.5 -> "$92.50".
export const formatMoney = (amount) => {
  const value = Number(amount) || 0;
  return Number.isInteger(value) ? `$${value}` : `$${value.toFixed(2)}`;
};

const round2 = (n) => Math.round(n * 100) / 100;

// The "enroll in any 2 courses" offer, derived from the active multi-course discount rules and the
// active paid courses (never hard-coded). Returns null when there is no rule that covers two courses
// or fewer than two courses it applies to.
export const getBundleOffer = (activeCourses, rules, isEnrolled = () => false) => {
  const rule = [...(rules || [])].sort((a, b) => b.min_courses - a.min_courses).find((r) => r.min_courses <= 2 && (r.max_courses == null || r.max_courses >= 2));
  if (!rule) return null;

  let applicable = null;
  if (!rule.apply_to_all_courses) {
    try {
      applicable = JSON.parse(rule.applicable_courses || '[]').map(Number);
    } catch (error) {
      return null;
    }
  }
  const eligible = activeCourses.filter((course) =>
    course.isPaidCourse && parseFloat(course.price) > 0 && !isEnrolled(course.id)
    && (!applicable || applicable.includes(Number(course.id))));
  if (eligible.length < 2) return null;

  const courses = eligible.slice(0, 2);
  const total = round2(courses.reduce((sum, course) => sum + parseFloat(course.price), 0));
  const value = parseFloat(rule.discount_value) || 0;
  const discount = round2(rule.discount_type === 'percentage' ? (total * value) / 100 : Math.min(value, total));
  if (discount <= 0) return null;

  return {
    courses,
    total,
    discount,
    finalPrice: round2(total - discount),
    // What each of the two costs inside the bundle, for the "or $90 each" hint on a card.
    eachWithBundle: (course) => round2(parseFloat(course.price) - discount / 2),
    eligibleIds: new Set(eligible.map((course) => course.id)),
  };
};
